import { requestMonarch } from "@red-digital/bricks";
import Cookies from "js-cookie";
import dynamic from "next/dynamic";
import { NextRouter } from "next/router";
import React, { FC, startTransition, useEffect, useState } from "react";
import { Provider as ReduxProvider } from "react-redux";
import { Store } from "redux";
import { ThemeProvider } from "styled-components";
import { DEFAULT_FUSE_NUMBER } from "~/constants/config/cohesion";
import { DEFAULT_API_VERSION, MONARCH_RULESETS, MONARCH_SOURCE_ID, MONARCH_TOKEN } from "~/constants/config/common";
import { getSiteData } from "~/helpers/site";
import { addressFormatAllconnect } from "~/helpers/addressFormatter";
import clientSide from "~/helpers/clientSide";
import { trackEvent } from "~/helpers/tracking";
import { setAddress } from "~/redux/actions/address";
import { setMonarchDecision, setPageLoading } from "~/redux/actions/common";
import { setNotifications } from "~/redux/actions/global";
import { EventData } from "~/types/events";
import { GlobalStyles, theme } from "../styles";
import { useStoreVariablesValidation } from "~/hooks/useSessionValidation";
import { MonarchDecisionPayload } from "~/types/common";
import { isOrderRouteNavigationUrl, normalizedPathFromNavigationUrl } from "~/helpers/orderLoaderRoute";

const Debugger = dynamic(() => import("../components/Debugger"), { ssr: false });

export interface FuelProviderCallbackProps {
  isOutageHappening: boolean;
}
interface FuelContextProps {
  router: NextRouter;
  production: boolean;
  trackEvent: ({ action, data, event }: EventData) => void;
  fuseNumber: string;
}

interface FuelProviderProps {
  children: JSX.Element[] | JSX.Element;
  store: Store;
  production: boolean;
  router: NextRouter;
  callback: (props: FuelProviderCallbackProps) => void;
}

export const FuelContext = React.createContext<FuelContextProps | null>(null);

const FuelProvider: FC<FuelProviderProps> = ({ children, store, router, production, callback }) => {
  const [fuseNumber, setFuseNumber] = useState(null);

  useEffect(() => {
    if (!router?.events?.on) return;

    const handleRouteChangeComplete = (url: string, routeProps?: { shallow: boolean }) => {
      const shallow = routeProps?.shallow ?? false;
      // Path-only match: avoid false positives from query strings (e.g. ...?foo=confirmation) and substrings like /order-confirmation
      const normalizedPath = typeof url === "string" ? normalizedPathFromNavigationUrl(url) : "/";
      const isConfirmation = normalizedPath.endsWith("/confirmation") || normalizedPath.endsWith("/confirmation-fiber");
      // /order and /order-fiber: do not clear here — useOrder clears on commit. Use only the *destination* URL:
      // `router` can still reflect the previous route when this fires, which skipped clearing pageLoading when leaving order.
      const isOrderOwnedLoaderPath = isOrderRouteNavigationUrl(url, router?.basePath);

      if (isConfirmation) {
        trackEvent({
          action: "elementViewed",
          data: {
            elementType: "PAGE TRANSITION",
            location: "/confirmation",
            position: "TRANSITION START",
            text: "tracking?.redirectUrl",
          },
        });
      }

      if (!shallow && !isConfirmation) {
        store.dispatch(setNotifications());
      }

      if (!isConfirmation && !isOrderOwnedLoaderPath) {
        startTransition(() => {
          store.dispatch(setPageLoading(false));
        });
      }
    };

    // If the SPA transition fails (chunk 404 from stale buildId, network error, route guard block, etc.)
    // Next.js emits routeChangeError. Always clear the loader so the user isn't stuck on an overlay.
    const handleRouteChangeError = () => {
      store.dispatch(setPageLoading(false));
    };

    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    router.events.on("routeChangeError", handleRouteChangeError);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      router.events.off("routeChangeError", handleRouteChangeError);
    };
  }, [router, store]);

  const monarchCallback = (err: unknown, monarchResponse: unknown) => {
    if (typeof monarchResponse === "object") {
      const rules = Object.entries(monarchResponse || {});
      const valueMap = { apiVersion: DEFAULT_API_VERSION } as MonarchDecisionPayload;

      if (rules.length) {
        rules.forEach(([key, returnValue]) => (valueMap[key] = returnValue));
        const isOutageHappening = valueMap.outageForm === true || valueMap.outageForm === "true";
        callback({ isOutageHappening });
        store.dispatch(setMonarchDecision(valueMap));
      }
    }
  };

  useEffect(() => {
    window?.cohesion("fuse:leasesReceived", (leases) => {
      setFuseNumber(leases?.[0]?.dnis);
    });

    window?.cohesion("fuse:error", () => setFuseNumber(DEFAULT_FUSE_NUMBER));
  }, []);

  useEffect(() => {
    if (clientSide) {
      const olVersionMlp = window?.sessionStorage?.getItem("olVersion");
      const apiVersionCookie = Cookies.get("apiVersion");
      const olVersion = apiVersionCookie ? apiVersionCookie : olVersionMlp;

      const payload = {
        ol_version: olVersion,
      };

      const params = {
        writeKey: getSiteData("writeKey"),
        makeSourceUid: getSiteData("sourceKey"),
        sourceId: MONARCH_SOURCE_ID,
        token: MONARCH_TOKEN,
      };

      // Wait for cohesion to be fully ready (which happens after automatic pageview events)
      window?.cohesion("ready", () => {
        // Ensure monarch is also ready before making the request
        window?.cohesion("monarch:ready", () => {
          requestMonarch({
            method: "ruleset",
            id: MONARCH_RULESETS.GLOBAL,
            payload,
            params,
            callback: monarchCallback,
          });
        });
      });
    }
  }, [router.query]);

  useEffect(() => {
    if (clientSide) {
      const addressCookie = Cookies.get("address");
      if (addressCookie) {
        const parsedAddress = JSON.parse(addressCookie);
        const formattedAddress = addressFormatAllconnect(parsedAddress);
        store.dispatch(setAddress("install", formattedAddress));
      }
    }
  }, [clientSide]);

  // Use the session validation hook
  const address = store.getState()?.address?.install?.address;
  const selectedProductId = store.getState()?.products?.selected?.internet?.productId;

  useStoreVariablesValidation(address, selectedProductId, router);

  return (
    <ReduxProvider store={store}>
      <FuelContext.Provider
        value={{
          production,
          router,
          trackEvent,
          fuseNumber,
        }}
      >
        <ThemeProvider theme={theme}>
          <GlobalStyles />
          {children}
          {!production && <Debugger />}
        </ThemeProvider>
      </FuelContext.Provider>
    </ReduxProvider>
  );
};

FuelProvider.displayName = "FuelProvider";

export default FuelProvider;
